import React, { useCallback, useEffect, useRef, useState, Fragment } from 'react';
import { LoaderIcon, AlertCircleIcon } from 'lucide-react';
import { HeroSection } from '../components/HeroSection';
import { TrustSignals } from '../components/TrustSignals';
import { DegreeCategoryGrid } from '../components/DegreeCategoryGrid';
import { SEOContentSection } from '../components/SEOContentSection';
import { FAQSection } from '../components/FAQSection';
import { searchSchools, MatchedSchool } from '../services/matchingApi';
import { MatchedSchoolCard, DidYouKnowBanner } from '../components/MatchedSchoolCard';
import { HomeInlineLeadCapture } from '../components/HomeInlineLeadCapture';
import { ProgramContentSection } from '../components/ProgramContentSection';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';
export function HomePage() {
  usePageMeta('OnlineCollegeGuide | Compare Accredited Online Degree Programs 2026', 'Compare accredited online colleges and universities. Get matched with degree programs that fit your career goals, schedule, and budget. No cost to students.', {
    ogImage: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const {
    areaStudy,
    program,
    degreeInterest: degreeInterestPath
  } = useParams<{
    areaStudy?: string;
    program?: string;
    degreeInterest?: string;
  }>();
  // Degree level for the initial listing: ?degreeInterest= wins over the
  // /degree/:area/:program/:degreeInterest path segment, matching the widget.
  const initialDegree = new URLSearchParams(window.location.search).get('degreeInterest')?.trim() || degreeInterestPath || '';
  const navigate = useNavigate();
  const [matchedSchools, setMatchedSchools] = useState<MatchedSchool[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  // Track selected values for the content section
  const [selectedDegree, setSelectedDegree] = useState<string>('');
  const [selectedArea, setSelectedArea] = useState<string>('');
  const [selectedProgram, setSelectedProgram] = useState<string>('');
  // On narrow screens the hero and its widget fill the viewport, so results
  // rendered below it are invisible without scrolling — the visitor submits and
  // appears to get nothing back. Bring the matches into view once they arrive.
  const resultsRef = useRef<HTMLDivElement>(null);
  const pendingScrollRef = useRef(false);
  // The last listing request sent, as "area|program|degree".
  const lastSearchKey = useRef('');
  const fetchResults = useCallback(async (degreeInterest: string, areaStudyStr: string, mProgram: string) => {
    // Only search if mProgram (specialization) is selected
    if (!mProgram) {
      setHasSearched(false);
      return;
    }
    setSelectedDegree(degreeInterest);
    setSelectedArea(areaStudyStr);
    setSelectedProgram(mProgram);
    // Keep the URL in step with the widget, including the degree level, so a
    // refresh or shared link reloads the same listing. Query params are kept.
    // Recorded before navigating so the route-change effect below recognizes
    // this search as already done and doesn't send it twice.
    lastSearchKey.current = `${areaStudyStr}|${mProgram}|${degreeInterest}`;
    if (areaStudyStr && mProgram && (areaStudy !== areaStudyStr || program !== mProgram || (degreeInterestPath || '') !== degreeInterest)) {
      const path = degreeInterest ? `/degree/${areaStudyStr}/${mProgram}/${degreeInterest}` : `/degree/${areaStudyStr}/${mProgram}`;
      // The path now carries the degree level, so a stale ?degreeInterest=
      // is dropped rather than left to override it on the next load.
      const query = new URLSearchParams(window.location.search);
      query.delete('degreeInterest');
      const qs = query.toString();
      navigate(`${path}${qs ? `?${qs}` : ''}`, {
        replace: true
      });
    }
    pendingScrollRef.current = true;
    setIsLoading(true);
    setApiError(null);
    setHasSearched(true);
    const result = await searchSchools({
      degreeInterest: degreeInterest || undefined,
      areaStudy: areaStudyStr || undefined,
      mProgram: mProgram || undefined
    });
    if (result.success) {
      setMatchedSchools(result.schools);
    } else {
      setApiError('Unable to load school matches. Please try again.');
      setMatchedSchools([]);
    }
    setIsLoading(false);
  }, [navigate, areaStudy, program, degreeInterestPath]);
  // On mount, if params exist, search — with the degree level when known.
  // Keyed on the route values only, so the URL sync above doesn't re-trigger
  // a second identical request.
  const initialSearchKey = `${areaStudy}|${program}|${initialDegree}`;
  useEffect(() => {
    if (!areaStudy || !program || lastSearchKey.current === initialSearchKey) return;
    lastSearchKey.current = initialSearchKey;
    fetchResults(initialDegree, areaStudy, program);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSearchKey]);
  // Mobile only — on desktop the results already sit beside the fold.
  useEffect(() => {
    if (isLoading || !hasSearched || !pendingScrollRef.current) return;
    const target = resultsRef.current;
    if (!target || window.innerWidth >= 1024) {
      pendingScrollRef.current = false;
      return;
    }
    pendingScrollRef.current = false;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const raf = requestAnimationFrame(() => {
      target.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start'
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [isLoading, hasSearched]);
  return <main className="flex-grow bg-light" data-id="element-2284" data-mp="3htq2qyufhqhg">
      <HeroSection onSelectionChange={fetchResults} data-id="element-2285" data-mp="3mqgdr7rdlvw0" />
      <TrustSignals data-id="element-2286" data-mp="3mr88kgd5fpl8" />

      {hasSearched ? <div ref={resultsRef} id="school-matches" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 scroll-mt-20" data-id="element-2287" data-mp="tw821zyx0sc3">
          <div className="flex flex-col lg:flex-row gap-8" data-id="element-2288" data-mp="oygd9etkhmc4">
            {/* Left Column: Results */}
            <div className="lg:w-2/3" data-id="element-2289" data-mp="12y8ywvq8lcd8">
              {/* Loading State */}
              {isLoading && <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-gray-200" data-id="element-2290" data-mp="xxtn48llscxd">
                  <LoaderIcon className="w-8 h-8 text-navy animate-spin mb-4" data-id="element-2291" data-mp="1ctstnet2e50e" />
                  <p className="text-gray-600 font-medium" data-id="element-2292" data-mp="17v7tffdtblby">
                    Finding your best school matches...
                  </p>
                </div>}

              {/* Error State */}
              {!isLoading && apiError && <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3" data-id="element-2293" data-mp="1hmlzy746llel">
                  <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-2294" data-mp="1vmdafxvhojnu" />
                  <div data-id="element-2295" data-mp="1qobpt8if0ca9">
                    <p className="text-red-800 font-medium" data-id="element-2296" data-mp="1qknx2bksntwl">{apiError}</p>
                    <p className="text-red-600 text-sm mt-1" data-id="element-2297" data-mp="2a93jxmjd9iki">
                      Try adjusting your search criteria or check back later.
                    </p>
                  </div>
                </div>}

              {/* Results */}
              {!isLoading && !apiError && matchedSchools.length > 0 && <div className="space-y-4" data-id="element-2298" data-mp="25865nl1h98oa">
                  {matchedSchools.map((school, idx) => <Fragment key={(school as Record<string, unknown>).schoolid as string || idx} data-id="element-2299" data-mp="1rh1b2xcyv6kt" data-mpi={idx}>
                      <MatchedSchoolCard school={school} index={idx} data-id="element-2300" data-mp="1w9uenhwp88vb" />
                      {idx === 0 && matchedSchools.length > 1 && <DidYouKnowBanner data-id="element-2301" data-mp="1hl7mgflz3mmo" />}
                    </Fragment>)}
                </div>}

              {/* Empty State */}
              {!isLoading && !apiError && matchedSchools.length === 0 && <div className="bg-white rounded-xl border border-gray-200 p-12 text-center" data-id="element-2302" data-mp="18k9ks3z0br79">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4" data-id="element-2303" data-mp="1cjqp2u2p6iau">
                    <AlertCircleIcon className="w-8 h-8 text-gray-400" data-id="element-2304" data-mp="1syztab96qqn7" />
                  </div>
                  <h3 className="text-xl font-bold text-navy mb-2" data-id="element-2305" data-mp="1o2j7votdeqdt">
                    No matches found
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto" data-id="element-2306" data-mp="1j47wvarca9u0">
                    Try adjusting your degree level or program selection to find
                    more results.
                  </p>
                </div>}
            </div>

            {/* Right Column: Inline Lead Capture */}
            <div className="lg:w-1/3" data-id="element-2307" data-mp="158nf4incjxo6">
              <HomeInlineLeadCapture data-id="element-2308" data-mp="10b41nkyh3oxa" />
            </div>
          </div>

          {/* Dynamic Program Content Section */}
          {!isLoading && !apiError && selectedProgram && <ProgramContentSection degreeInterest={selectedDegree} areaOfStudy={selectedArea} specialization={selectedProgram} data-id="element-2309" data-mp="vaprnxdba4wm" />}
        </div> : <>
          <DegreeCategoryGrid data-id="element-2310" data-mp="mcxbkqzrmt86" />
          <SEOContentSection data-id="element-2311" data-mp="mcnvbv4iw0fo" />
          <FAQSection data-id="element-2312" data-mp="2xz7uvmvlm0rv" />
        </>}
    </main>;
}